import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';

import { StoreState } from '../../reducers';
import Page from '../../containers/Page';
import { PageType } from '../../containers/Page/constants';
import { getProfileData, saveProfileData } from '../../containers/ProfilePage/actions';
import { AvatarSize, ProfilePageFields } from '../../containers/ProfilePage/reducer';
import UserAvatar from '../../containers/ProfilePage/UserAvatar';
import EditableUserAvatar from '../../containers/ProfilePage/EditableUserAvatar';
import UserInfo from '../../containers/ProfilePage/UserInfo';
import CRMSelector from '../../containers/ProfilePage/CRMSelector';
import EmailIntegration from '../../containers/ProfilePage/EmailIntegration';
import Accordion from '../Accordion';
import {
	Header,
	UserInfoWrapper,
	Loader,
	ButtonSave,
	ButtonLogout,
	Input,
	Error,
	Message,
} from './styles';
import { requestSignOut } from '../../containers/MultiAppAuthentication/actions';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import UserAgentUtils from '../../utils/userAgent';

interface ProfilePageProps {
	clientId: number;
	name: string;
	email: string;
	position: string;
	phone: string;
	isLoading: boolean;
	isProfileLoaded: boolean;
	crmProviders: Api.CRMProviderData[];
	emailProvider: Api.EmailProviderData;
	isReadOnlyApp: boolean;
	dispatch: Dispatch<StoreState>;
}

interface ProfilePageState extends ProfilePageFields {
	editedFields: {
		[k: string]: boolean;
	};
	errors: {
		[k: string]: boolean;
	};
	crmProviders: Api.CRMProviderData[] | null;
}

enum FieldNames {
	name = 'name',
	email = 'email',
	position = 'position',
	phone = 'phone',
	password = 'password',
	confirmPassword = 'confirmPassword',
}

const NAME_MIN_LENGTH = 4;
const PASSWORD_MIN_LENGTH = 6;

const isError = (name: FieldNames, value: string, data: { [k: string]: string } = {}) => (
	(name === FieldNames.name && value.length < NAME_MIN_LENGTH) ||
	(name === FieldNames.password && value && value.length < PASSWORD_MIN_LENGTH) ||
	(name === FieldNames.confirmPassword && value !== data.password)
);

const ProfilePage = ({
	clientId,
	name: initialName,
	email: initialEmail,
	position: initialPosition,
	phone: initialPhone,
	isLoading,
	isProfileLoaded,
	crmProviders,
	emailProvider,
	isReadOnlyApp,
	dispatch,
}: ProfilePageProps) => {
	const initialState: ProfilePageState = {
		name: initialName,
		position: initialPosition,
		phone: initialPhone,
		password: '',
		confirmPassword: '',
		crmProviders: null,
		editedFields: {},
		errors: {},
	};
	const [state, setState] = React.useState<ProfilePageState>(initialState);

	const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const editedFields = Object.keys(state.editedFields).reduce((acc, key) => {
			if (state.editedFields[key]) {
				acc[key] = state[key];
			}
			return acc;
		}, {} as ProfilePageFields);

		dispatch(saveProfileData(editedFields));
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setState(state => ({
			...state,
			[name]: value,
			editedFields: {
				...state.editedFields,
				[name]: true,
			},
			errors: {
				...state.errors,
				[name]: isError(name as FieldNames, value),
			},
		}));
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setState(state => ({
			...state,
			[name]: value,
			editedFields: {
				...state.editedFields,
				[name]: !!value, // do not save empty password
			},
			errors: {
				...state.errors,
				[name]: isError(name as FieldNames, value),
				[FieldNames.confirmPassword]: isError(FieldNames.confirmPassword, confirmPassword, { password: value }),
			},
		}));
	};

	const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setState(state => ({
			...state,
			[name]: value,
			editedFields: {
				...state.editedFields,
				[name]: !!value, // do not save empty password
			},
			errors: {
				...state.errors,
				[name]: isError(name as FieldNames, value, { password }),
			},
		}));
	};

	const handleCRMProviderChange = (crmProvider: Api.CRMProviders) => {
		setState(state => ({
			...state,
			crmProviders: crmProvider === Api.CRMProviders.NONE ? [] : null,
			editedFields: {
				...state.editedFields,
				crmProviders: true,
			},
		}));
	};

	React.useEffect(() => {
		dispatch(getProfileData(clientId));
	}, [clientId]);

	React.useEffect(() => {
		if (!isLoading) {
			setState(initialState);
		}
	}, [isLoading]);

	const {
		name,
		position,
		phone,
		password,
		confirmPassword,
		errors,
	} = state;
	const isSaveDisabled = Object.values(errors).includes(true);

	return (
		<Page
			pageType={PageType.ProfilePage}
			title="messages.my_profile"
		>
			{isLoading &&
				<Loader>
					{Utils.getSvgIcon('iconset_loading')}
					<Translate value="messages.loading" />
				</Loader>
			}
			<Header>
				{!UserAgentUtils.isOnline() && !isProfileLoaded &&
					<Message value="messages.profile_offline_message" />
				}
				{isProfileLoaded &&
					<UserInfoWrapper>
						{UserAgentUtils.isOnline()
							? <EditableUserAvatar />
							: <UserAvatar size={AvatarSize.BIG} />
						}
						<UserInfo />
					</UserInfoWrapper>
				}
				<ButtonLogout
					type="button"
					onClick={() => dispatch(requestSignOut())}
				>
					<Translate value="messages.log_out" />
				</ButtonLogout>
			</Header>
			{!UserAgentUtils.isOnline() && isProfileLoaded &&
				<Message value="messages.profile_offline_message" />
			}
			{UserAgentUtils.isOnline() && isProfileLoaded &&
				<form onSubmit={handleFormSubmit} className="ProfileForm">
					<Accordion
						title="messages.profile"
						isOpened={true}
					>
						{errors.name &&
							<Error
								value="error.field_must_have_at_least"
								charsCount={NAME_MIN_LENGTH.toString()}
							/>
						}
						<Input
							type="text"
							name={FieldNames.name}
							value={name}
							placeholder={I18n.t('messages.name_surname')}
							isError={errors.name}
							onChange={handleInputChange}
						/>
						<Input
							type="text"
							name={FieldNames.position}
							value={position}
							placeholder={I18n.t('messages.position')}
							onChange={handleInputChange}
						/>
						<Input
							type="text"
							name={FieldNames.email}
							value={initialEmail}
							readOnly={true}
						/>
						<Input
							type="text"
							name={FieldNames.phone}
							value={phone}
							placeholder={I18n.t('messages.phone_number')}
							onChange={handleInputChange}
						/>
					</Accordion>
					<Accordion
						title="messages.password"
						isOpened={true}
					>
						{errors.password &&
							<Error
								value="error.field_must_have_at_least"
								charsCount={PASSWORD_MIN_LENGTH.toString()}
							/>
						}
						<Input
							type="password"
							name={FieldNames.password}
							value={password}
							placeholder={I18n.t('messages.change_password')}
							isError={errors.password}
							onChange={handlePasswordChange}
						/>
						{errors.confirmPassword &&
							<Error value="error.passwords_do_not_match" />
						}
						<Input
							type="password"
							name={FieldNames.confirmPassword}
							value={confirmPassword}
							placeholder={I18n.t('messages.confirm_password')}
							isError={errors.confirmPassword}
							onChange={handleConfirmPasswordChange}
						/>
					</Accordion>
					{!isReadOnlyApp &&
						<>
							{crmProviders && crmProviders.length > 0 &&
								<CRMSelector onChange={handleCRMProviderChange} />
							}
							{emailProvider && <EmailIntegration />}
						</>
					}
					<ButtonSave
						type="submit"
						disabled={isSaveDisabled}
					>
						<Translate value="messages.save" />
					</ButtonSave>
				</form>
			}
		</Page>
	);
};

export default ProfilePage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ProfilePage/index.tsx